import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Radio } from 'antd'
import PerText from '../../components/custom_components/Text'
import { PerPopoverFooter } from '../../components/custom_components/PopoverFooter/PopoverFooter'
import { EmployeeTagType, EmployeeTagDto, Country, TalentsSeasonStatus } from '../../types'
import { RadioChangeEvent } from 'antd/lib/radio'
import PerFlexRow from '../../components/custom_components/FlexRow'
import useHandleEmployeeTags from '../../graphql/ranking/hooks/useHandleEmployeeTag'
import shallow from 'zustand/shallow'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'

type RankingSetTagPopoverProps = {
  isVisible: boolean
  companyId: string
  country: Country
  setIsVisible: (value: boolean) => void
  impactTags: EmployeeTagDto[]
  riskTags: EmployeeTagDto[]
  employeeId: string
}

const EmployeeTags = { ...EmployeeTagType, ...{ unassigned: 'unassigned' } }

const filterTags = (tags: EmployeeTagDto[]) => {
  return !!tags.length ? tags[0].type : EmployeeTags.unassigned
}

export const RankingRetentionPopover: FC<RankingSetTagPopoverProps> = ({
  isVisible,
  setIsVisible,
  companyId,
  country,
  impactTags,
  riskTags,
  employeeId,
}) => {
  const { t } = useTranslation()
  const [employeeImpactTag, setImpactTag] = useState<string>(filterTags(impactTags))
  const [employeeRiskTag, setRiskTag] = useState<string>(filterTags(riskTags))
  const [isProcessing, toggleProcessing] = useState<boolean>(false)

  const [talentsSeasonSettings] = useCultivatingTalentsStore((state) => [state.talentsSeasonSettings], shallow)
  const referenceYear = talentsSeasonSettings ? talentsSeasonSettings.year : new Date().getUTCFullYear()
  const rankingInProgress = talentsSeasonSettings
    ? [TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed].includes(talentsSeasonSettings.talentsSeasonStatus)
    : false

  const { addEmployeeTag, removeEmployeeTag } = useHandleEmployeeTags(companyId, country, referenceYear, rankingInProgress)

  const handleOnCancel = () => {
    setIsVisible(!isVisible)
  }

  const handleConfirm = async () => {
    toggleProcessing(true)

    const initialTags = [...impactTags, ...riskTags].map((tag) => tag.type)
    const addedTags = [employeeImpactTag, employeeRiskTag]
      .filter((tag) => tag !== EmployeeTags.unassigned)
      .filter((tag) => !initialTags.includes(tag))
    const removedTags = initialTags.filter((tag) => ![employeeImpactTag, employeeRiskTag].includes(tag))

    if (!!removedTags.length) {
      await Promise.all(removedTags.map((tag) => removeEmployeeTag({ employeeId, type: tag as EmployeeTagType })))
    }

    if (!!addedTags.length) {
      await Promise.all(addedTags.map((tag) => addEmployeeTag(employeeId, tag as EmployeeTagType)))
    }

    setIsVisible(!isVisible)
  }

  const onImpactChange = (e: RadioChangeEvent) => {
    setImpactTag(e.target.value || EmployeeTags.unassigned)
  }

  const onRiskChange = (e: RadioChangeEvent) => {
    setRiskTag(e.target.value || EmployeeTags.unassigned)
  }

  return (
    <>
      <PerText appearance="label" fontSize="xs" color="secondary" display="block" mb="xxs">
        {t('rankingView.table.actions.tagEmployeeRetention.groups.impact')}
      </PerText>

      <Radio.Group value={employeeImpactTag} onChange={onImpactChange}>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.unassigned}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.general.${EmployeeTags.unassigned}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.ImpactOfLossCritical}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.impactOfLoss.${EmployeeTags.ImpactOfLossCritical}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.ImpactOfLossSignificant}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.impactOfLoss.${EmployeeTags.ImpactOfLossSignificant}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.ImpactOfLossModerate}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.impactOfLoss.${EmployeeTags.ImpactOfLossModerate}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.ImpactOfLossMinor}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.impactOfLoss.${EmployeeTags.ImpactOfLossMinor}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
      </Radio.Group>

      <PerText appearance="label" fontSize="xs" color="secondary" display="block" mb="xxs" mt="s">
        {t('rankingView.table.actions.tagEmployeeRetention.groups.risk')}
      </PerText>

      <Radio.Group value={employeeRiskTag} onChange={onRiskChange}>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.unassigned}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.general.${EmployeeTags.unassigned}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.RiskOfLossVeryHigh}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.riskOfLoss.${EmployeeTags.RiskOfLossVeryHigh}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.RiskOfLossHigh}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.riskOfLoss.${EmployeeTags.RiskOfLossHigh}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.RiskOfLossMedium}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.riskOfLoss.${EmployeeTags.RiskOfLossMedium}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.RiskOfLossLow}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.riskOfLoss.${EmployeeTags.RiskOfLossLow}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
      </Radio.Group>

      <PerPopoverFooter onClickCancel={handleOnCancel} onClickConfirm={handleConfirm} isProcessing={isProcessing} />
    </>
  )
}
